// Compare products feature
//
// Stores the ids of the products a shopper wants to compare in localStorage (capped at MAX_PRODUCTS). Product cards
// and the product page expose a <button is="compare-button"> to toggle a product in/out of the list. A global
// <compare-bar> shows how many products are selected. The "Compare products" section fetches the section markup
// through the /search endpoint (same technique used by the theme's own recently-viewed-products section) so that
// the comparison table is rendered server-side with full Liquid/currency/translation support.
(() => {
  const STORAGE_KEY = 'theme:compare-products';
  const CHANGE_EVENT = 'theme:compare:change';
  const MAX_PRODUCTS = 3;

  function getCompareList() {
    try {
      const ids = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(ids) ? ids : [];
    } catch (error) {
      return [];
    }
  }

  function setCompareList(ids) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
    } catch (error) {
      // Safari private mode (and similar) can throw when storage is unavailable; fail silently.
    }

    document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { ids } }));
  }

  function addToCompare(id) {
    const ids = getCompareList();

    if (ids.includes(id)) {
      return true;
    }

    if (ids.length >= MAX_PRODUCTS) {
      return false;
    }

    setCompareList([...ids, id]);
    return true;
  }

  function removeFromCompare(id) {
    setCompareList(getCompareList().filter((existingId) => existingId !== id));
  }

  function clearCompare() {
    setCompareList([]);
  }

  class CompareButton extends HTMLButtonElement {
    connectedCallback() {
      this.productId = parseInt(this.getAttribute('data-product-id'), 10);
      this._onClick = this._onClick.bind(this);
      this._onChange = this._render.bind(this);

      this.addEventListener('click', this._onClick);
      document.addEventListener(CHANGE_EVENT, this._onChange);
      window.addEventListener('storage', this._onChange);

      this._render();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      document.removeEventListener(CHANGE_EVENT, this._onChange);
      window.removeEventListener('storage', this._onChange);
    }

    _onClick() {
      if (!this.productId) {
        return;
      }

      const isActive = getCompareList().includes(this.productId);

      if (isActive) {
        removeFromCompare(this.productId);
      } else if (!addToCompare(this.productId)) {
        this._flashLimitReached();
      }
    }

    _render() {
      const ids = getCompareList();
      const isActive = ids.includes(this.productId);
      const atLimit = ids.length >= MAX_PRODUCTS;

      this.classList.toggle('is-active', isActive);
      this.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      this.disabled = !isActive && atLimit;

      let label = this.dataset.labelAdd || '';

      if (isActive) {
        label = this.dataset.labelRemove || '';
        this.title = '';
      } else if (this.disabled) {
        label = this.dataset.labelMax || '';
        this.title = label;
      } else {
        this.title = '';
      }

      this.setAttribute('aria-label', label);

      const textElement = this.querySelector('[data-compare-button-text]');
      if (textElement) {
        textElement.textContent = label;
      }
    }

    _flashLimitReached() {
      this.classList.remove('compare-button--shake');
      // Force reflow so the animation can be re-triggered on consecutive clicks.
      void this.offsetWidth;
      this.classList.add('compare-button--shake');
    }
  }

  class CompareBar extends HTMLElement {
    connectedCallback() {
      this._onChange = this._render.bind(this);
      this._onClick = this._onClick.bind(this);

      document.addEventListener(CHANGE_EVENT, this._onChange);
      window.addEventListener('storage', this._onChange);
      this.addEventListener('click', this._onClick);

      this._render();
    }

    disconnectedCallback() {
      document.removeEventListener(CHANGE_EVENT, this._onChange);
      window.removeEventListener('storage', this._onChange);
      this.removeEventListener('click', this._onClick);
    }

    _onClick(event) {
      if (event.target.closest('[data-compare-clear]')) {
        clearCompare();
      }
    }

    _render() {
      const ids = getCompareList();

      this.hidden = ids.length === 0;
      this.classList.toggle('is-full', ids.length >= MAX_PRODUCTS);

      const countElement = this.querySelector('[data-compare-count]');
      if (countElement && countElement.dataset.template) {
        countElement.textContent = countElement.dataset.template.replace('__COUNT__', ids.length);
      }

      const linkElement = this.querySelector('[data-compare-link]');
      if (linkElement) {
        const disabled = ids.length < 2;
        linkElement.setAttribute('aria-disabled', disabled ? 'true' : 'false');
        linkElement.classList.toggle('is-disabled', disabled);
      }
    }
  }

  class CompareProducts extends HTMLElement {
    connectedCallback() {
      this._onChange = this._loadProducts.bind(this);
      this._onClick = this._onClick.bind(this);

      document.addEventListener(CHANGE_EVENT, this._onChange);
      window.addEventListener('storage', this._onChange);
      this.addEventListener('click', this._onClick);

      this._loadProducts();
    }

    disconnectedCallback() {
      document.removeEventListener(CHANGE_EVENT, this._onChange);
      window.removeEventListener('storage', this._onChange);
      this.removeEventListener('click', this._onClick);
    }

    _onClick(event) {
      const removeButton = event.target.closest('[data-compare-remove]');
      if (removeButton) {
        removeFromCompare(parseInt(removeButton.getAttribute('data-compare-remove'), 10));
        return;
      }

      if (event.target.closest('[data-compare-clear]')) {
        clearCompare();
      }
    }

    async _loadProducts() {
      const ids = getCompareList();
      const emptyState = this.querySelector('[data-compare-empty]');
      const tableContainer = this.querySelector('[data-compare-table]');

      if (ids.length === 0) {
        if (tableContainer) {
          tableContainer.hidden = true;
          tableContainer.innerHTML = '';
        }

        if (emptyState) {
          emptyState.hidden = false;
        }

        return;
      }

      if (emptyState) {
        emptyState.hidden = true;
      }

      const section = this.closest('.shopify-section');
      if (!section || !tableContainer) {
        return;
      }

      const sectionId = section.id.replace('shopify-section-', '');
      const queryString = ids.map((id) => `id:${id}`).join(' OR ');
      const url = `${window.Shopify.routes.root}search?type=product&q=${queryString}&section_id=${sectionId}`;

      try {
        const response = await fetch(url);
        const html = await response.text();
        const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
        const newTableContainer = parsedDocument.querySelector('[data-compare-table]');

        if (newTableContainer) {
          tableContainer.innerHTML = newTableContainer.innerHTML;
          tableContainer.hidden = false;
        }
      } catch (error) {
        // Network failure: leave the previous state in place rather than showing a broken table.
      }
    }
  }

  if (!window.customElements.get('compare-button')) {
    window.customElements.define('compare-button', CompareButton, { extends: 'button' });
  }

  if (!window.customElements.get('compare-bar')) {
    window.customElements.define('compare-bar', CompareBar);
  }

  if (!window.customElements.get('compare-products')) {
    window.customElements.define('compare-products', CompareProducts);
  }
})();
